'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  balance: 20000,
  rate: 6.5,
  payment: 400,
  extraPayment: 100,
};

const PRESETS = {
  conservative: {
    balance: 15000,
    rate: 5.0,
    payment: 500,
    extraPayment: 200,
  },
  moderate: {
    balance: 20000,
    rate: 6.5,
    payment: 400,
    extraPayment: 100,
  },
  aggressive: {
    balance: 30000,
    rate: 8.0,
    payment: 300,
    extraPayment: 50,
  },
};

export default function CalculatorLoanPayoff() {
  const [balance, setBalance] = useState(INITIAL_STATE.balance);
  const [rate, setRate] = useState(INITIAL_STATE.rate);
  const [payment, setPayment] = useState(INITIAL_STATE.payment);
  const [extraPayment, setExtraPayment] = useState(INITIAL_STATE.extraPayment);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (setter: (v: number) => void, value: number) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const calculatePayoff = (extra: number) => {
    let remainingBalance = balance;
    let months = 0;
    let totalInterest = 0;
    const monthlyRate = rate / 100 / 12;
    const totalPayment = payment + extra;

    while (remainingBalance > 0 && months < 600) {
      const interestCharge = remainingBalance * monthlyRate;
      const principalPayment = Math.min(totalPayment - interestCharge, remainingBalance);
      totalInterest += interestCharge;
      remainingBalance -= principalPayment;
      months++;
    }

    return { months, totalInterest };
  };

  const withoutExtra = calculatePayoff(0);
  const withExtra = calculatePayoff(extraPayment);
  const monthsSaved = withoutExtra.months - withExtra.months;
  const interestSaved = withoutExtra.totalInterest - withExtra.totalInterest;

  const handleReset = () => {
    setBalance(INITIAL_STATE.balance);
    setRate(INITIAL_STATE.rate);
    setPayment(INITIAL_STATE.payment);
    setExtraPayment(INITIAL_STATE.extraPayment);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setBalance(values.balance);
    setRate(values.rate);
    setPayment(values.payment);
    setExtraPayment(values.extraPayment);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Current Balance</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={balance}
                onChange={(e) => handleChange(setBalance, Number(e.target.value))}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Interest Rate</label>
            <div className="relative">
              <input
                type="number"
                value={rate}
                onChange={(e) => handleChange(setRate, Number(e.target.value))}
                step={0.1}
                className="w-full pl-3 pr-10 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Monthly Payment</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={payment}
                onChange={(e) => handleChange(setPayment, Number(e.target.value))}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Extra Monthly Payment</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={extraPayment}
                onChange={(e) => handleChange(setExtraPayment, Number(e.target.value))}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Payoff Time</div>
            <div className="text-5xl font-bold">{withExtra.months} months</div>
            <div className="text-sm opacity-90 mt-2">{(withExtra.months / 12).toFixed(1)} years</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-lg p-4 text-center border-2 border-green-200">
              <div className="text-xs text-green-700 mb-1">Time Saved</div>
              <div className="text-xl font-bold text-green-900">{monthsSaved} months</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center border-2 border-green-200">
              <div className="text-xs text-green-700 mb-1">Interest Saved</div>
              <div className="text-xl font-bold text-green-900">${interestSaved.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}